/**
 * 生产大屏 v357 · 工厂车间挂屏 24/7 显示
 *
 * v200 v213 port · 复用 TN_BIGSCREEN 设计:
 *   - 13 列工单实时表 (单号 / 交期 / 客户 / 产品 / 工艺 / 机台 / 进度条)
 *   - 60s 自动刷新 (operator dashboard 标准)
 *   - TV mode 集成 (F11 全屏 + 隐 NAV/sidebar)
 *   - Andon ticker 底部告警条 (复用 v351 AndonTicker)
 *   - 加急 🔥 顶置 · 逾期红色 pulse
 */
import { query } from '@core/db';
import { renderPageHeader } from '@widgets/KPICard';
import { registerAutoRefresh } from '@services/autoRefresh';
import { mountAndonTicker } from '@widgets/AndonTicker';

interface WO {
  id: number; code: string; product_name: string; customer_name: string;
  planned_qty: number; completed_qty: number; status: string; urgency: string;
  due_date: string | null; equipment_name: string; process_name: string; worker_name: string;
}

const STATUS_LABEL: Record<string, { zh: string; cls: string }> = {
  planned:      { zh: '⏳ 待开工', cls: 'bg-gray-700 text-gray-200' },
  in_progress:  { zh: '🏭 生产中', cls: 'bg-blue-700 text-blue-100' },
  printing:     { zh: '🖨️ 印刷中', cls: 'bg-purple-700 text-purple-100' },
  die_cutting:  { zh: '✂️ 模切中', cls: 'bg-orange-700 text-orange-100' },
  gluing:       { zh: '🪣 粘合中', cls: 'bg-cyan-700 text-cyan-100' },
  qc:           { zh: '🛡️ 品检中', cls: 'bg-amber-700 text-amber-100' },
  completed:    { zh: '✅ 已完工', cls: 'bg-green-700 text-green-100' },
};

export async function render(): Promise<HTMLElement> {
  const c = document.createElement('div');
  c.className = 'tn-bigscreen';

  // 顶部 banner (TV mode 时可隐)
  c.appendChild(renderPageHeader('🏭', '生产大屏 · Production Big Screen', '车间挂屏 · 60s 自动刷新 · F11 进 TV mode 全屏'));

  const wrap = document.createElement('div');
  wrap.id = 'tn-bigscreen-prod-wrap';
  wrap.className = 'mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4';
  c.appendChild(wrap);

  // 自动刷新数据
  const refresh = (): void => renderTable(wrap);
  refresh(); // 立即第一次渲染

  // TV mode 自动 Andon · 60s 刷新
  if (document.body.dataset.tvMode === '1') {
    mountAndonTicker();
  }
  registerAutoRefresh('bigScreenProduction', refresh, 60_000);

  return c;
}

function renderTable(wrap: HTMLElement): void {
  const today = new Date().toISOString().slice(0, 10);
  const rows = safeQuery<WO>(`
    SELECT wo.id, wo.code,
      COALESCE(p.name, '-') AS product_name,
      COALESCE(cu.name, '-') AS customer_name,
      COALESCE(wo.planned_qty, 0) AS planned_qty,
      COALESCE(wo.completed_qty, 0) AS completed_qty,
      COALESCE(wo.status, 'planned') AS status,
      COALESCE(wo.urgency, 'medium') AS urgency,
      wo.due_date,
      COALESCE(e.name, '-') AS equipment_name,
      COALESCE(proc.name_cn, '-') AS process_name,
      COALESCE(emp.name, '-') AS worker_name
    FROM work_orders wo
    LEFT JOIN products p ON p.id = wo.product_id
    LEFT JOIN sales_orders so ON so.id = wo.so_id
    LEFT JOIN customers cu ON cu.id = so.customer_id
    LEFT JOIN equipment e ON e.id = wo.equipment_id
    LEFT JOIN processes proc ON proc.id = wo.current_process_id
    LEFT JOIN employees emp ON emp.id = wo.assigned_to
    WHERE COALESCE(wo.deleted_at, '') = ''
      AND COALESCE(wo.status, '') NOT IN ('completed', 'done', 'closed')
    ORDER BY
      CASE WHEN wo.urgency = 'urgent' THEN 0 ELSE 1 END,
      COALESCE(wo.due_date, '9999-12-31') ASC,
      wo.id DESC
    LIMIT 25
  `);

  const tableHtml = rows.length === 0
    ? `<div class="text-center py-12 text-gray-400 text-2xl">✅ 当前无在产工单 · 车间休息中</div>`
    : `
    <table class="w-full text-sm" style="border-collapse:collapse">
      <thead style="background:#0a1838;color:#dbeafe;position:sticky;top:0;z-index:5">
        <tr style="border-bottom:2px solid #1e40af">
          <th class="px-2 py-2 text-left">#</th>
          <th class="px-2 py-2 text-left">工单号</th>
          <th class="px-2 py-2 text-left">客户 / 产品</th>
          <th class="px-2 py-2 text-center">工艺</th>
          <th class="px-2 py-2 text-center">机台</th>
          <th class="px-2 py-2 text-center">作业员</th>
          <th class="px-2 py-2 text-right">计划 / 完成</th>
          <th class="px-2 py-2 text-left" style="width:160px">进度</th>
          <th class="px-2 py-2 text-center">交期</th>
          <th class="px-2 py-2 text-center">状态</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map((r, i) => {
          const pct = r.planned_qty > 0 ? Math.min(100, Math.round(r.completed_qty / r.planned_qty * 100)) : 0;
          const overdue = r.due_date && r.due_date < today;
          const urgent = r.urgency === 'urgent';
          const meta = STATUS_LABEL[r.status] || { zh: r.status, cls: 'bg-gray-600 text-gray-200' };
          const barColor = urgent ? '#ef4444' : overdue ? '#dc2626' : pct >= 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b';
          return `
            <tr style="border-bottom:1px solid #1f2937;${urgent ? 'background:rgba(239,68,68,0.08);' : ''}${overdue ? 'animation:tnBsPulse 1.6s infinite;' : ''}">
              <td class="px-2 py-2 text-gray-500">${i + 1}</td>
              <td class="px-2 py-2 font-mono ${urgent ? 'text-red-300 font-bold' : 'text-gray-300'}">${urgent ? '🔥 ' : ''}${escapeHtml(r.code)}</td>
              <td class="px-2 py-2"><div class="font-semibold">${escapeHtml(r.customer_name)}</div><div class="text-xs text-gray-500">${escapeHtml(r.product_name)}</div></td>
              <td class="px-2 py-2 text-center">${escapeHtml(r.process_name)}</td>
              <td class="px-2 py-2 text-center font-mono text-xs">${escapeHtml(r.equipment_name)}</td>
              <td class="px-2 py-2 text-center">${escapeHtml(r.worker_name)}</td>
              <td class="px-2 py-2 text-right font-mono"><span class="text-green-400">${r.completed_qty.toLocaleString()}</span> / ${r.planned_qty.toLocaleString()}</td>
              <td class="px-2 py-2">
                <div style="background:#1e293b;border-radius:8px;height:18px;overflow:hidden;position:relative">
                  <div style="background:${barColor};height:100%;width:${pct}%;transition:width 0.6s;display:flex;align-items:center;justify-content:flex-end;padding-right:6px;color:#fff;font-size:11px;font-weight:600">${pct}%</div>
                </div>
              </td>
              <td class="px-2 py-2 text-center font-mono ${overdue ? 'text-red-400 font-bold' : 'text-gray-300'}">${r.due_date ? r.due_date.slice(5) : '-'}${overdue ? ' 🚨' : ''}</td>
              <td class="px-2 py-2 text-center"><span class="${meta.cls}" style="padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600">${meta.zh}</span></td>
            </tr>
          `;
        }).join('')}
      </tbody>
    </table>
  `;

  wrap.innerHTML = `
    <style>
      .tn-bigscreen { background: linear-gradient(135deg, #0a1838 0%, #1a2d6b 100%); color: #dbeafe; min-height: 100vh; padding: 16px; }
      .tn-bigscreen table { color: #dbeafe; }
      .tn-bigscreen th { font-size: 13px; font-weight: 700; letter-spacing: 0.02em; }
      .tn-bigscreen td { font-size: 13px; }
      .tn-bigscreen tr:hover { background: rgba(59,130,246,0.05); }
      @keyframes tnBsPulse { 0%,100%{background:rgba(220,38,38,0.18);}50%{background:rgba(220,38,38,0.32);} }
      body[data-tv-mode] .tn-bigscreen { font-size: 18px; }
      body[data-tv-mode] .tn-bigscreen th { font-size: clamp(14px, 1.2vw, 18px); }
      body[data-tv-mode] .tn-bigscreen td { font-size: clamp(13px, 1.1vw, 17px); }
    </style>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-2xl font-bold text-blue-200">🏭 在产工单 · ${rows.length} 单</h2>
      <div class="text-sm text-gray-400">⏰ ${new Date().toLocaleTimeString('zh-CN', { hour12: false })} · 60s 自动刷新</div>
    </div>
    ${tableHtml}
  `;
}

function safeQuery<T>(sql: string, params?: unknown[]): T[] {
  try { return query<T>(sql, params); } catch { return []; }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
